import { CircleX, Columns3, Plus, Rows3, TableProperties, Trash2, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getPatternLineStats, type PatternAxis, type PatternLineAction } from '../core/adjustPatternLine';
import type { PatternProject } from '../types';

interface Props {
  project: PatternProject;
  maxSize: number;
  onClose: () => void;
  onConfirm: (axis: PatternAxis, action: PatternLineAction, index: number, count: number) => void;
}

const MIN_SIZE = 16;

export function PatternLineAdjustModal({ project, maxSize, onClose, onConfirm }: Props) {
  const [axis, setAxis] = useState<PatternAxis>('row');
  const [action, setAction] = useState<PatternLineAction>('delete');
  const [position, setPosition] = useState(1);
  const [count, setCount] = useState(1);
  const positionRef = useRef<HTMLInputElement>(null);
  const currentLength = axis === 'row' ? project.height : project.width;
  const maxPosition = action === 'insert' ? currentLength + 1 : currentLength;
  const availableCount = action === 'insert' ? maxSize - currentLength : currentLength - MIN_SIZE;
  const maxCount = action === 'insert'
    ? availableCount
    : Math.min(availableCount, Math.max(0, currentLength - position + 1));
  const validPosition = Number.isInteger(position) && position >= 1 && position <= maxPosition;
  const validCount = Number.isInteger(count) && count >= 1 && count <= maxCount;
  const blockedBySize = availableCount < 1;
  const stats = useMemo(
    () => action === 'delete' && validPosition && validCount ? getPatternLineStats(project, axis, position - 1, count) : null,
    [action, axis, count, position, project, validCount, validPosition]
  );
  const delta = validCount ? (action === 'insert' ? count : -count) : 0;
  const nextWidth = project.width + (axis === 'column' ? delta : 0);
  const nextHeight = project.height + (axis === 'row' ? delta : 0);
  const axisLabel = axis === 'row' ? '行' : '列';
  const rangeLabel = count === 1 ? `第 ${position}` : `第 ${position}-${position + count - 1}`;

  useEffect(() => {
    setPosition((value) => Math.min(Math.max(1, value), maxPosition));
  }, [maxPosition]);

  useEffect(() => {
    setCount((value) => Math.min(Math.max(1, value), Math.max(1, maxCount)));
  }, [maxCount]);

  useEffect(() => {
    positionRef.current?.focus();
    positionRef.current?.select();
  }, []);

  return <div className="modal-backdrop editor-dialog-layer" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="editor-dialog line-adjust-dialog" role="dialog" aria-modal="true" aria-labelledby="line-adjust-title">
      <button className="editor-dialog-close" type="button" onClick={onClose} aria-label="关闭行列调整"><CircleX size={20} /></button>
      <div className="editor-dialog-heading"><span className="editor-dialog-icon violet"><TableProperties size={21} /></span><div><p className="eyebrow">ROW &amp; COLUMN</p><h2 id="line-adjust-title">单独调整行列</h2><p>指定位置插入或删除一整行/列，其余内容会顺序平移，不再从画布边缘整块裁切。</p></div></div>

      <fieldset className="line-adjust-options">
        <legend>调整方向</legend>
        <div className="line-adjust-segments">
          <button className={axis === 'row' ? 'active' : ''} type="button" onClick={() => setAxis('row')} aria-pressed={axis === 'row'}><Rows3 size={17} /><span><strong>行</strong><small>横向一整行</small></span></button>
          <button className={axis === 'column' ? 'active' : ''} type="button" onClick={() => setAxis('column')} aria-pressed={axis === 'column'}><Columns3 size={17} /><span><strong>列</strong><small>纵向一整列</small></span></button>
        </div>
      </fieldset>

      <fieldset className="line-adjust-options">
        <legend>操作方式</legend>
        <div className="line-adjust-segments">
          <button className={action === 'insert' ? 'active insert' : ''} type="button" onClick={() => setAction('insert')} aria-pressed={action === 'insert'}><Plus size={17} /><span><strong>插入空白{axisLabel}</strong><small>原位置及之后顺移</small></span></button>
          <button className={action === 'delete' ? 'active delete' : ''} type="button" onClick={() => setAction('delete')} aria-pressed={action === 'delete'}><Trash2 size={17} /><span><strong>删除指定{axisLabel}</strong><small>后面的内容自动补位</small></span></button>
        </div>
      </fieldset>

      <div className="line-adjust-position">
        <label htmlFor="line-adjust-position">起始{axisLabel}
          <span><input ref={positionRef} id="line-adjust-position" type="number" inputMode="numeric" min="1" max={maxPosition} value={position} onChange={(event) => setPosition(Number(event.target.value))} /><b>/ {maxPosition}</b></span>
        </label>
        <label htmlFor="line-adjust-count">连续数量
          <span><input id="line-adjust-count" type="number" inputMode="numeric" min="1" max={Math.max(1, maxCount)} value={count} onChange={(event) => setCount(Number(event.target.value))} /><b>最多 {Math.max(0, maxCount)}</b></span>
        </label>
        <div><small>尺寸变化</small><strong>{project.width} × {project.height} <i>→</i> {nextWidth} × {nextHeight}</strong></div>
      </div>

      {action === 'insert'
        ? <div className="line-adjust-note insert"><Plus size={17} /><span><strong>将在第 {position || '-'} {axisLabel}前插入 {count || '-'} 个空白{axisLabel}</strong><small>{position === currentLength + 1 ? `添加在最后一${axisLabel}之后` : `原第 ${position || '-'} ${axisLabel}及后续内容顺移 ${count || '-'} 格`}，已有豆子与完成状态都会保留。</small></span></div>
        : <div className="line-adjust-note delete"><TriangleAlert size={18} /><span><strong>将永久删除{validPosition && validCount ? rangeLabel : '指定范围'} {axisLabel}</strong><small>{stats ? `这些${axisLabel}共包含 ${stats.beadCount} 颗豆${stats.completedCount ? `，其中 ${stats.completedCount} 颗已完成` : ''}` : `请输入有效的起始${axisLabel}和连续数量`}；确认后仍可使用“撤销”恢复。</small></span></div>}

      {blockedBySize && <p className="line-adjust-limit">{action === 'delete' ? `${axis === 'row' ? '高度' : '宽度'}不能小于 ${MIN_SIZE} 格，当前不能继续删除。` : `${axis === 'row' ? '高度' : '宽度'}已达到账号上限 ${maxSize} 格，当前不能继续插入。`}</p>}

      <footer className="editor-dialog-actions"><button className="soft-dialog-button" type="button" onClick={onClose}>取消</button><button className={action === 'delete' ? 'danger-dialog-button' : 'primary-button compact'} type="button" disabled={!validPosition || !validCount || blockedBySize} onClick={() => onConfirm(axis, action, position - 1, count)}>{action === 'delete' ? <Trash2 size={15} /> : <Plus size={15} />}确认{action === 'delete' ? '删除' : '插入'} {validCount ? count : '-'} {axisLabel}</button></footer>
    </section>
  </div>;
}
